/**
 * planner_approve_keywords — seam-independent core.
 *
 * Split from the command itself so the argument schema, the gate predicates
 * and the diff projection can be written, typechecked and unit-tested before
 * Calendar X2Y1's commit lands the planner seams on ReadDeps/WriteDeps. When
 * `approveKeyword` exists, `plannerApproveKeywords.ts` wires resolve/run to it
 * and imports everything below unchanged.
 *
 * Nothing here touches the network, the store, or a page handler.
 */
import { z } from 'zod';

import { refuse, type Refusal } from '../contract';

// ── Arguments ────────────────────────────────────────────────────────────────
// Hoisted to module scope deliberately. big-calendar re-registers on every
// store tick because it passes inline schema literals; a schema defined once
// cannot drift between registrations.

/**
 * Feed rows are addressed by id, or by the keyword text an agent actually has
 * in hand. Keyword text is resolved to ids and refused with candidates when it
 * is ambiguous, so the model never invents an id.
 */
export const approveKeywordsArgs = z
  .object({
    ids: z.array(z.string().uuid()).min(1).max(25).optional(),
    keywords: z.array(z.string().min(2)).min(1).max(25).optional(),
    /**
     * Explicit acknowledgement that a keyword vetting `swap` verdict is being
     * overridden. Default false: a swap verdict is refused, never auto-cleared.
     * The page guards this with a window.confirm, which no tool path may reach
     * (the browser blocks it and page code cannot answer it), and on this
     * branch the server does not gate it either — so this argument is the only
     * cannibalization guard the agent path has.
     */
    acknowledge_cannibalization: z.boolean().default(false),
  })
  .refine((a) => Boolean(a.ids?.length) || Boolean(a.keywords?.length), {
    message: 'Provide ids or keywords.',
  });

export type ApproveKeywordsArgs = z.infer<typeof approveKeywordsArgs>;

// ── The feed row, narrowed to what the gates actually read ───────────────────

export interface FeedCandidate {
  readonly id: string;
  readonly primary_keyword: string;
  readonly status: string;
  readonly duplicate_status: string;
  readonly hermes_verdict?: string | null;
  readonly hermes_suggested_swap?: string | null;
  readonly target_month?: string | null;
}

/** Server accepts an approve only from these. Anything else is already spent. */
const APPROVABLE_STATUSES: ReadonlySet<string> = new Set(['candidate', 'stale', 'approved']);

export function isApprovable(row: FeedCandidate): boolean {
  return APPROVABLE_STATUSES.has(row.status);
}

/** The server gate: approve requires a clear duplicate verdict. */
export function isDuplicateClear(row: FeedCandidate): boolean {
  return row.duplicate_status === 'clear';
}

export function isSwapVerdict(row: FeedCandidate): boolean {
  return row.hermes_verdict === 'swap';
}

// ── Gate ─────────────────────────────────────────────────────────────────────

/**
 * Every reason a row cannot be approved, checked before anything is written so
 * a batch never half-lands. Returns null when the row may proceed.
 *
 * Ordering is deliberate: duplicate and status are properties of the row, while
 * the swap refusal is a judgement the operator can override, so it is reported
 * last and names the alternative rather than just declining.
 */
export function gateCandidate(
  row: FeedCandidate,
  acknowledgeCannibalization: boolean,
): Refusal | null {
  if (!isApprovable(row)) {
    return refuse(
      `"${row.primary_keyword}" is ${row.status}, which cannot be approved.`,
      { retryable: false },
    );
  }
  if (!isDuplicateClear(row)) {
    return refuse(
      `"${row.primary_keyword}" is ${row.duplicate_status}, and approve requires duplicate_status=clear.`,
      { retryable: false },
    );
  }
  if (isSwapVerdict(row) && !acknowledgeCannibalization) {
    const alternative = row.hermes_suggested_swap || 'an alternative keyword';
    return refuse(
      `Keyword vetting flagged "${row.primary_keyword}" as likely cannibalization and suggests "${alternative}". ` +
        'Approving anyway needs acknowledge_cannibalization: true.',
      { retryable: true },
    );
  }
  return null;
}

// ── Diff projection ──────────────────────────────────────────────────────────

/**
 * What approving actually changes, including the part no caller asks for.
 *
 * keyword_feed_service.py:671-677: an explicit target_month always wins; when
 * the status becomes approved and the month is still null the backend fills it
 * with the next calendar-month bucket; a non-null month is never overwritten.
 * That auto-fill is a real row change, so the diff states it rather than
 * letting a date appear out of nowhere after the write.
 */
export function projectApproval(
  row: FeedCandidate,
  nextMonthBucket: string,
): readonly { field: string; from: string | null; to: string }[] {
  const changes: { field: string; from: string | null; to: string }[] = [
    { field: 'status', from: row.status, to: 'approved' },
  ];
  if (!row.target_month) {
    changes.push({ field: 'target_month', from: null, to: nextMonthBucket });
  }
  return changes;
}

/** `YYYY-MM` for the month after the supplied date. Mirrors the backend bucket. */
export function nextCalendarMonthBucket(from: Date): string {
  const year = from.getUTCFullYear();
  const month = from.getUTCMonth(); // 0-indexed
  const next = new Date(Date.UTC(year, month + 1, 1));
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  return `${next.getUTCFullYear()}-${mm}`;
}

// ── Selector resolution ──────────────────────────────────────────────────────

export interface ResolutionOutcome {
  readonly matched: readonly FeedCandidate[];
  readonly refusal: Refusal | null;
}

/**
 * Loose agent input to exact rows. A miss or an ambiguity returns the candidate
 * list inside the refusal, which is what lets a write tool stand in for a read
 * tool when the agent guessed wrong.
 */
export function resolveSelectors(
  args: ApproveKeywordsArgs,
  pool: readonly FeedCandidate[],
): ResolutionOutcome {
  const byId = new Map(pool.map((row) => [row.id, row]));
  const matched: FeedCandidate[] = [];

  for (const id of args.ids ?? []) {
    const row = byId.get(id);
    if (!row) {
      return {
        matched: [],
        refusal: refuse(`No feed item ${id} for this tenant.`, {
          candidates: pool.slice(0, 10).map((c) => ({ id: c.id, label: c.primary_keyword })),
        }),
      };
    }
    matched.push(row);
  }

  for (const keyword of args.keywords ?? []) {
    const needle = keyword.trim().toLowerCase();
    const hits = pool.filter((row) => row.primary_keyword.toLowerCase().includes(needle));
    if (hits.length === 0) {
      return {
        matched: [],
        refusal: refuse(`No feed keyword matches "${keyword}".`, {
          candidates: pool.slice(0, 10).map((c) => ({ id: c.id, label: c.primary_keyword })),
        }),
      };
    }
    if (hits.length > 1) {
      return {
        matched: [],
        refusal: refuse(`"${keyword}" matches ${hits.length} feed items; approve takes exact ids.`, {
          candidates: hits.slice(0, 10).map((c) => ({ id: c.id, label: c.primary_keyword })),
        }),
      };
    }
    matched.push(hits[0]);
  }

  const deduped = [...new Map(matched.map((row) => [row.id, row])).values()];
  return { matched: deduped, refusal: null };
}
